import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { setAccessToken } from '../dal/api-config'
import { resendVerificationEmail } from '../dal/user-dal'
import { addBreadcrumb } from '../utils/sentry'
import { queryKeys } from './query-keys'

interface CredentialQueryParams {
  authUserId: string | null
  getCredentials: () => Promise<{
    accessToken: string | null
    idToken: string | null
  }>
}

export function credentialQueryOptions({
  authUserId,
  getCredentials,
}: CredentialQueryParams) {
  return queryOptions({
    queryKey: queryKeys.user.credentials(authUserId),
    queryFn: async () => {
      if (authUserId == null) {
        throw new Error('credentialQuery called without a userId')
      }

      const credentials = await getCredentials()
      const accessToken = credentials.accessToken ?? null
      const hasAccessToken =
        typeof accessToken === 'string' && accessToken.length > 0

      setAccessToken(accessToken)

      if (!hasAccessToken) {
        addBreadcrumb(
          'auth',
          'missing_access_token_for_user',
          { userId: authUserId },
          'warning',
        )
      }

      return hasAccessToken
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: true,
    placeholderData: (prev: boolean | undefined) => prev,
  })
}

export function useResendVerificationEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['settings', 'resend-verification-email'],
    mutationFn: resendVerificationEmail,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.user.emailVerification.all(),
      })
    },
  })
}
